import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Box, Paper, Skeleton, useMediaQuery, alpha } from '@mui/material';
import { Button, IconButton, Typography } from '@sm-dashboard/ui-components';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AllActivitiesDialog, { ActivityItem } from './AllActivitiesDialog';
import { Purchase, Donation, Lead } from '@sm-dashboard/types';
import { getJewishDay } from 'jewish-dates-core';
import dayjs, { type Dayjs } from 'dayjs';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MessageIcon from '@mui/icons-material/Message';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useFetch } from '@/hooks/useFetch';
import { donationsUrl, purchasesUrl, leadsUrl } from '@/constants';
import { getUser } from '@/util/getUser';
import { useAppContext } from '@/context/context';
import { getContactDisplayName } from '@/util/contactUtils';

const CRM_CONTACTS_URL = 'pvt/crm/contacts';
const MOBILE_ACTIVITIES_VISIBLE = 2;

const RecentActivitiesList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const user = getUser();
  const { userPermission } = useAppContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const activitiesContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const { data: donationsData, loading: donationsLoading, fetchData: fetchDonations } = useFetch();
  const { data: purchasesData, loading: purchasesLoading, fetchData: fetchPurchases } = useFetch();
  const { data: leadsData, loading: leadsLoading, fetchData: fetchLeads } = useFetch();
  const { data: contactsData, loading: contactsLoading, fetchData: fetchContacts } = useFetch();
  const loading = donationsLoading || purchasesLoading || leadsLoading || contactsLoading;

  useEffect(() => {
    if (!user?.id) return;

    const queryParams = new URLSearchParams({
      page: '0',
      rowsPerPage: '1000'
    });

    if (userPermission?.operations?.length) {
      queryParams.append('operationsAllowed', userPermission.operations.join(','));
      queryParams.append('shaliachIdAllow', String(user.id));
    }
    queryParams.append('shaliachId', String(user.id));

    fetchDonations({
      method: 'GET',
      url: `${donationsUrl}?${queryParams.toString()}`
    });

    fetchPurchases({
      method: 'GET',
      url: `${purchasesUrl}?${queryParams.toString()}`
    });

    fetchLeads({
      method: 'GET',
      url: `${leadsUrl}?${queryParams.toString()}`
    });

    const contactsQueryParams = new URLSearchParams({
      page: '0',
      rowsPerPage: '1000',
      shaliachId: String(user.id)
    });

    fetchContacts({
      method: 'GET',
      url: `${CRM_CONTACTS_URL}?${contactsQueryParams.toString()}`
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userPermission?.operations]);

  const donations = useMemo(() => (donationsData?.rows || []) as Donation[], [donationsData?.rows]);
  const purchases = useMemo(() => (purchasesData?.rows || []) as Purchase[], [purchasesData?.rows]);
  const leads = useMemo(() => (leadsData?.rows || []) as Lead[], [leadsData?.rows]);
  const contacts = useMemo(
    () =>
      (contactsData?.rows || []).map(
        (contact: {
          id?: number;
          fullName?: string;
          firstName?: string;
          lastName?: string;
          name?: string;
          createdAt: string;
          comment?: string;
          phone?: string;
        }) => ({
          id: contact.id,
          fullName: contact.fullName,
          firstName: contact.firstName,
          lastName: contact.lastName,
          name: contact.name,
          createdAt: contact.createdAt,
          comment: contact.comment,
          phone: contact.phone
        })
      ),
    [contactsData?.rows]
  );

  const activities = useMemo(() => {
    const allActivities: ActivityItem[] = [];

    donations.forEach(donation => {
      allActivities.push({
        id: donation.id,
        type: 'donation',
        createdAt: donation.createdAt,
        name: donation.name || 'ללא שם',
        amount: donation.amount,
        comment: donation.comment,
        phone: donation.phone
      });
    });

    purchases.forEach(purchase => {
      allActivities.push({
        id: purchase.id,
        type: 'purchase',
        createdAt: purchase.createdAt,
        name: purchase.name || 'ללא שם',
        amount: purchase.amount,
        products: purchase.products,
        comment: purchase.comment,
        phone: purchase.phone
      });
    });

    leads.forEach(lead => {
      allActivities.push({
        id: lead.id,
        type: 'lead',
        createdAt: lead.createdAt,
        name: lead.name || 'ללא שם',
        comment: lead.comment,
        phone: lead.phone
      });
    });

    contacts.forEach(
      (contact: {
        id?: number;
        fullName?: string;
        firstName?: string;
        lastName?: string;
        name?: string;
        createdAt: string;
        comment?: string;
        phone?: string;
      }) => {
        const contactName = (() => {
          const fromFirstLast = getContactDisplayName(contact);
          return fromFirstLast !== 'ללא שם' ? fromFirstLast : contact.name || 'ללא שם';
        })();

        allActivities.push({
          id: contact.id || '',
          type: 'contact',
          createdAt: contact.createdAt,
          name: contactName,
          comment: contact.comment,
          phone: contact.phone
        });
      }
    );

    return allActivities.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [donations, purchases, leads, contacts]);

  const getActivityIcon = (type: ActivityItem['type']): React.ReactElement => {
    const iconSx = { fontSize: { xs: 16, md: 18 }, color: 'text.primary', flexShrink: 0 };
    switch (type) {
      case 'donation':
        return <VolunteerActivismIcon sx={iconSx} />;
      case 'purchase':
        return <ShoppingCartIcon sx={iconSx} />;
      case 'lead':
        return <MessageIcon sx={iconSx} />;
      case 'contact':
        return <PeopleIcon sx={iconSx} />;
      default:
        return <MessageIcon sx={iconSx} />;
    }
  };

  const getActivityLabel = (type: ActivityItem['type']) => {
    switch (type) {
      case 'donation':
        return 'תרומה';
      case 'purchase':
        return 'רכישה';
      case 'lead':
        return 'פנייה';
      case 'contact':
        return 'איש קשר';
      default:
        return 'פעילות';
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'donation':
        return alpha(theme.palette.success.main, 0.2);
      case 'purchase':
        return alpha(theme.palette.error.main, 0.2);
      case 'lead':
        return alpha(theme.palette.info.main, 0.2);
      case 'contact':
        return alpha(theme.palette.secondary.main, 0.2);
      default:
        return theme.palette.grey.A200;
    }
  };

  const formatActivityDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const gregorianDate = date.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const time = date.toLocaleTimeString('he-IL', {
        hour: '2-digit',
        minute: '2-digit'
      });

      let hebrewDate = '';
      try {
        const jewishDay = getJewishDay(dayjs(date) as Dayjs);
        if (jewishDay?.jewishDateStrHebrew) {
          hebrewDate = jewishDay.jewishDateStrHebrew;
        }
      } catch (e) {
        // ignore
      }

      return hebrewDate ? `${hebrewDate}, ${gregorianDate}, ${time}` : `${gregorianDate}, ${time}`;
    } catch (e) {
      return dateString;
    }
  };

  const getActivityDescription = (activity: ActivityItem) => {
    if (activity.type === 'donation' && activity.amount) {
      const amount = parseFloat(activity.amount);
      return `תרומה מ${activity.name} בסך ${amount.toLocaleString('he-IL')} ש"ח`;
    }
    if (activity.type === 'purchase' && activity.amount) {
      const amount = parseFloat(activity.amount);
      const productsText = activity.products ? ` - ${activity.products}` : '';
      return `רכישה מ${activity.name} בסך ${amount.toLocaleString('he-IL')} ש"ח${productsText}`;
    }
    if (activity.type === 'lead') {
      return `פנייה חדשה מ${activity.name}${activity.comment ? ` - ${activity.comment}` : ''}`;
    }
    if (activity.type === 'contact') {
      return `נוסף איש קשר חדש: ${activity.name}${activity.comment ? ` - ${activity.comment}` : ''}`;
    }
    return activity.comment || 'פעילות';
  };

  const handleNavigateToActivity = useCallback(
    (activity: ActivityItem) => {
      const basePath = {
        donation: '/desk/donations',
        purchase: '/desk/purchases',
        lead: '/desk/leads',
        contact: '/crm/contacts'
      }[activity.type];

      if (basePath && activity.id) {
        navigate(`${basePath}?id=${String(activity.id)}`);
      }
    },
    [navigate]
  );

  const calculateVisibleCount = useCallback(() => {
    if (loading || activities.length === 0) {
      return;
    }

    if (isMobile) {
      setVisibleCount(Math.min(MOBILE_ACTIVITIES_VISIBLE, activities.length));
      return;
    }

    if (!containerRef.current || !buttonRef.current || !activitiesContainerRef.current) {
      return;
    }

    const containerHeight = containerRef.current.offsetHeight;
    const buttonHeight = buttonRef.current.offsetHeight;
    const availableHeight = containerHeight - buttonHeight;

    if (availableHeight <= 0) {
      return;
    }

    const activityElements = activitiesContainerRef.current.children;

    if (activityElements.length === 0) {
      return;
    }

    let totalHeight = 0;
    let count = 0;

    for (let i = 0; i < activityElements.length; i++) {
      const element = activityElements[i] as HTMLElement;
      const elementHeight = element.offsetHeight;

      if (totalHeight + elementHeight <= availableHeight) {
        totalHeight += elementHeight;
        count++;
      } else {
        break;
      }
    }

    if (count > 0) {
      setVisibleCount(count);
    }
  }, [activities.length, loading, isMobile]);

  useEffect(() => {
    if (loading) {
      setVisibleCount(0);
      return;
    }

    if (activities.length === 0) {
      setVisibleCount(0);
      return;
    }
    if (visibleCount === 0) {
      const initial = isMobile ? MOBILE_ACTIVITIES_VISIBLE : 10;
      setVisibleCount(Math.min(initial, activities.length));
    }

    const timer = setTimeout(() => {
      calculateVisibleCount();
    }, 300);

    return () => clearTimeout(timer);
  }, [activities.length, loading, calculateVisibleCount, visibleCount, isMobile]);

  useEffect(() => {
    if (isMobile || !containerRef.current || loading) {
      return;
    }

    let timeoutId: NodeJS.Timeout;
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!loading) {
          calculateVisibleCount();
        }
      }, 100);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [calculateVisibleCount, loading, isMobile]);

  const visibleActivities = useMemo(() => {
    if (isMobile) {
      return activities.slice(0, Math.min(MOBILE_ACTIVITIES_VISIBLE, activities.length));
    }
    if (visibleCount === 0 && activities.length > 0) {
      return activities.slice(0, 5);
    }
    return activities.slice(0, visibleCount);
  }, [activities, visibleCount, isMobile]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1, mb: 2 }} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1, mb: 2 }} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1, mb: 2 }} />
      </Box>
    );
  }

  if (activities.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ color: 'text.secondary' }}>
          <Typography variant="body">לא נמצאו פעילויות</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        flexShrink: { xs: 0, md: 1 }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'common.white',
          p: 1,
          pl: 2,
          borderRadius: '30px 30px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexDirection: 'row',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <AssessmentIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Typography variant="h6">הפעילויות האחרונות</Typography>
        </Box>
      </Box>

      {/* Activities List */}
      <Paper
        ref={containerRef}
        sx={{
          borderRadius: '0 0 20px 20px',
          overflow: 'hidden',
          bgcolor: 'common.white',
          display: 'flex',
          flexDirection: 'column',
          flex: { xs: 'none', md: 1 },
          minHeight: { xs: 'auto', md: 0 }
        }}
      >
        <Box
          ref={activitiesContainerRef}
          sx={{ overflow: 'hidden', flex: { xs: 'none', md: 1 }, minHeight: { xs: 'auto', md: 0 } }}
        >
          {visibleActivities.map(activity => (
            <Box
              key={`${activity.type}-${activity.id}`}
              sx={{
                p: { xs: 1.25, md: 2 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {
                  borderBottom: 'none',
                  pb: 0
                },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 0.5, md: 1 }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexDirection: 'row-reverse',
                    bgcolor: getActivityColor(activity.type),
                    color: 'text.primary',
                    fontWeight: 600,
                    minWidth: { xs: 72, md: 100 },
                    height: { xs: 24, md: 26 },
                    borderRadius: '13px',
                    px: { xs: 0.75, md: 1 },
                    fontSize: { xs: '0.7rem', md: '0.8125rem' },
                    lineHeight: 1
                  }}
                >
                  {getActivityIcon(activity.type)}
                  {getActivityLabel(activity.type)}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: { xs: 1, md: 2 },
                  flexDirection: 'row-reverse'
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <IconButton
                    color="primary"
                    shape="circle"
                    size="small"
                    aria-label="מעבר לפעילות"
                    onClick={e => {
                      e.stopPropagation();
                      handleNavigateToActivity(activity);
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: 15, transform: 'rotate(180deg)' }} />
                  </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <Box sx={{ mb: { xs: 0.25, md: 0.5 } }}>
                    <Typography variant="body" color="text.secondary">
                      {formatActivityDate(activity.createdAt)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      wordBreak: 'break-word',
                      display: { xs: '-webkit-box', md: 'block' },
                      WebkitLineClamp: { xs: 2, md: 'unset' },
                      WebkitBoxOrient: { xs: 'vertical', md: 'unset' },
                      overflow: { xs: 'hidden', md: 'visible' }
                    }}
                  >
                    <Typography variant="body" color="text.primary">
                      {getActivityDescription(activity)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        {/* Button to view all activities */}
        <Box
          ref={buttonRef}
          sx={{
            pt: { xs: 1.25, md: 3 },
            pb: { xs: 1.25, md: 3 },
            px: { xs: 1.25, md: 2 },
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'background.default',
            mt: { xs: 0, md: 'auto' },
            flexShrink: 0
          }}
        >
          <Button
            fullWidth
            color="warning"
            variant="contained"
            onClick={() => setOpenDialog(true)}
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: '1rem', transform: 'rotate(180deg)' }} />}
          >
            ליומן הפעילויות
          </Button>
        </Box>
      </Paper>

      {/* Dialog with all activities */}
      <AllActivitiesDialog open={openDialog} onClose={() => setOpenDialog(false)} />
    </Box>
  );
};

export default RecentActivitiesList;
